import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/authFetch';

const API_URL = process.env.REACT_APP_API_URL || '';

const formatCAD = (cents) => {
  const dollars = (cents || 0) / 100;
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(dollars);
};

const BackorderAlertBanner = () => {
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const fetchSummary = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/order-modifications/backorders/summary`);
        if (response.ok) {
          const json = await response.json();
          setSummary(json.data);
        }
      } catch (err) {
        // Silently fail — banner is non-critical
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [user]);

  if (loading || dismissed || !summary || summary.total_backorders === 0) {
    return null;
  }

  const hasCPAWarning = summary.cpa_deadline_within_7_days > 0;
  const bgColor = hasCPAWarning ? '#C8614A' : '#2B8FAD';

  return (
    <div style={{
      background: bgColor,
      color: '#ffffff',
      padding: '10px 16px',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      position: 'relative'
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
          <span style={{ fontWeight: 600 }}>
            {summary.total_backorders} order{summary.total_backorders !== 1 ? 's' : ''} with backordered items
            {' '}&mdash; {formatCAD(summary.revenue_at_risk_cents)} revenue at risk
          </span>
        </div>
        {hasCPAWarning && (
          <div style={{ marginTop: '4px', fontWeight: 700, fontSize: '13px' }}>
            ⚠ {summary.cpa_deadline_within_7_days} order{summary.cpa_deadline_within_7_days !== 1 ? 's' : ''} approaching CPA deadline (30-day cancellation window)
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={() => navigate('/backorders')}
          style={{
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.2)',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          View Backorders
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss backorder alert"
          style={{
            background: 'none',
            border: 'none',
            color: '#ffffff',
            cursor: 'pointer',
            padding: '4px',
            lineHeight: 1,
            fontSize: '18px',
            opacity: 0.8
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default BackorderAlertBanner;
